/**
 * Serviciu pentru tracking utilizare utilizatori
 * Trimite datele de utilizare la backend pentru statistici
 */

const API_BASE = 'https://backend.alephmedia.ro/api'

// Cache pentru sesiuni active
const activeSessions = new Map()

// Queue pentru sesiuni care trebuie trimise
const sessionQueue = []

// Flag pentru a verifica dacă tracking-ul este activ
let isTrackingEnabled = true

/**
 * Obține token-ul de autentificare
 */
function getAuthToken() {
  return localStorage.getItem('authToken')
}

/**
 * Verifică dacă utilizatorul este autentificat
 */
function isAuthenticated() {
  return !!getAuthToken()
}

/**
 * Detectează platforma
 */
function detectPlatform() {
  const userAgent = navigator.userAgent || ''
  if (/android/i.test(userAgent)) {
    return 'android'
  } else if (/iphone|ipad|ipod/i.test(userAgent)) {
    return 'ios'
  }
  return 'web'
}

/**
 * Obține device ID (pentru mobile) sau generează unul pentru web
 */
function getDeviceId() {
  let deviceId = localStorage.getItem('device_id')
  if (!deviceId) {
    deviceId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem('device_id', deviceId)
  }
  return deviceId
}

/**
 * Trimite o sesiune la backend
 */
async function sendSession(sessionData) {
  if (!isAuthenticated() || !isTrackingEnabled) {
    return
  }

  try {
    const token = getAuthToken()
    const response = await fetch(`${API_BASE}/usage/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        platform: sessionData.platform || detectPlatform(),
        device_id: sessionData.device_id || getDeviceId(),
        app_version: sessionData.app_version || null,
        os_version: sessionData.os_version || null,
        session_start: sessionData.session_start || new Date().toISOString(),
        session_end: sessionData.session_end || new Date().toISOString(),
        duration_seconds: sessionData.duration_seconds || 0,
        content_type: sessionData.content_type || null,
        content_id: sessionData.content_id || null,
        metadata: sessionData.metadata || null
      })
    })

    if (!response.ok) {
      // Dacă eșuează, adăugăm în queue pentru retry
      sessionQueue.push(sessionData)
    }
  } catch (error) {
    // Dacă eșuează, adăugăm în queue pentru retry
    sessionQueue.push(sessionData)
  }
}

/**
 * Trimite mai multe sesiuni într-un batch
 */
async function sendBatchSessions(sessions) {
  if (!isAuthenticated() || !isTrackingEnabled || sessions.length === 0) {
    return
  }

  try {
    const token = getAuthToken()
    const response = await fetch(`${API_BASE}/usage/track-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ sessions })
    })

    if (!response.ok) {
      // Dacă eșuează, adăugăm în queue
      sessionQueue.push(...sessions)
    } else {
      // Ștergem sesiunile trimise cu succes din queue
      sessionQueue.splice(0, sessions.length)
    }
  } catch (error) {
    // Dacă eșuează, adăugăm în queue
    sessionQueue.push(...sessions)
  }
}

/**
 * Începe o sesiune de tracking
 */
function startSession(contentType, contentId, metadata = null) {
  if (!isAuthenticated() || !isTrackingEnabled) {
    return null
  }

  const sessionId = `${contentType}_${contentId}_${Date.now()}`
  const sessionStart = new Date()

  activeSessions.set(sessionId, {
    sessionId,
    contentType,
    contentId,
    metadata,
    sessionStart,
    platform: detectPlatform(),
    device_id: getDeviceId()
  })

  return sessionId
}

/**
 * Finalizează o sesiune de tracking
 */
function endSession(sessionId) {
  if (!sessionId || !activeSessions.has(sessionId)) {
    return
  }

  const session = activeSessions.get(sessionId)
  const sessionEnd = new Date()
  const durationSeconds = Math.floor((sessionEnd - session.sessionStart) / 1000)

  // Trimitem sesiunea
  sendSession({
    platform: session.platform,
    device_id: session.device_id,
    session_start: session.sessionStart.toISOString(),
    session_end: sessionEnd.toISOString(),
    duration_seconds: durationSeconds,
    content_type: session.contentType,
    content_id: session.contentId,
    metadata: session.metadata
  })

  // Ștergem sesiunea activă
  activeSessions.delete(sessionId)
}

/**
 * Track o sesiune completă (start + end)
 */
function trackSession(contentType, contentId, durationSeconds, metadata = null) {
  if (!isAuthenticated() || !isTrackingEnabled) {
    return
  }

  const sessionStart = new Date()
  const sessionEnd = new Date(sessionStart.getTime() + durationSeconds * 1000)

  sendSession({
    platform: detectPlatform(),
    device_id: getDeviceId(),
    session_start: sessionStart.toISOString(),
    session_end: sessionEnd.toISOString(),
    duration_seconds: durationSeconds,
    content_type: contentType,
    content_id: contentId,
    metadata
  })
}

/**
 * Track o sesiune de vizionare video/episod
 */
function trackVideoSession(contentType, contentId, startTime, endTime, metadata = null) {
  if (!isAuthenticated() || !isTrackingEnabled) {
    return
  }

  const durationSeconds = Math.floor((endTime - startTime) / 1000)
  if (durationSeconds <= 0) {
    return
  }

  trackSession(contentType, contentId, durationSeconds, metadata)
}

/**
 * Track navigare pe homepage
 */
function trackHomepageView() {
  if (!isAuthenticated() || !isTrackingEnabled) {
    return
  }

  // Track o sesiune scurtă de navigare
  trackSession('homepage', null, 1, { action: 'view' })
}

/**
 * Retry sesiunile din queue
 */
async function retryQueuedSessions() {
  if (sessionQueue.length === 0 || !isAuthenticated()) {
    return
  }

  // Luăm primele 50 de sesiuni din queue
  const sessionsToRetry = sessionQueue.splice(0, 50)
  if (sessionsToRetry.length > 0) {
    await sendBatchSessions(sessionsToRetry)
  }
}

// Retry queue-ul la fiecare 30 de secunde
if (typeof window !== 'undefined') {
  setInterval(retryQueuedSessions, 30000)
  
  // Retry când utilizatorul revine pe tab
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      retryQueuedSessions()
    }
  })
}

export {
  startSession,
  endSession,
  trackSession,
  trackVideoSession,
  trackHomepageView,
  isAuthenticated,
  detectPlatform,
  getDeviceId
}

